/**
 * CR111D: Preview Diagnostics Overlay (Dev-only)
 * 
 * Shows real-time diagnostic info in the preview area to help
 * correlate crashes with specific control changes.
 */

import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Activity, Clock, Hash, Layers, Zap } from "lucide-react";

interface PreviewDiagnosticsOverlayProps {
  /** "live" | "static" - is this a live preview with overrides? */
  mode: "live" | "static";
  /** Current deal type being previewed */
  dealType?: string;
  /** Hash or length of draft tokens/template for quick comparison */
  draftHash?: string | number;
  /** Last action that changed the preview (e.g., "changed fontHeading to Inter") */
  lastAction?: string;
  /** Render count (increments each render) */
  renderCount?: number;
  /** Time to render in ms (optional) */
  renderTimeMs?: number;
  /** Whether to show the overlay */
  show?: boolean;
}

/**
 * Simple hash for debugging - not cryptographic, just for quick comparison
 */
export function computeDraftHash(obj: unknown): string {
  try {
    const str = JSON.stringify(obj);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16).slice(0, 8);
  } catch {
    return "error";
  }
}

/**
 * Set the last preview action for global error logging
 */
export function setLastPreviewAction(action: string): void {
  if (typeof window !== "undefined") {
    (window as unknown as { __lastPreviewAction?: string }).__lastPreviewAction = `${action} @ ${new Date().toISOString()}`;
    console.log("[PreviewDiagnostics] Action:", action);
  }
}

/**
 * Log preview toggle event
 */
export function logPreviewToggle(enabled: boolean, context: string): void {
  const msg = `LIVE_PREVIEW_TOGGLE ${enabled ? "ON" : "OFF"} in ${context}`;
  setLastPreviewAction(msg);
}

export function PreviewDiagnosticsOverlay({
  mode,
  dealType,
  draftHash,
  lastAction,
  renderCount,
  renderTimeMs,
  show = true,
}: PreviewDiagnosticsOverlayProps) {
  // Only show in development
  const isDev = import.meta.env.DEV;
  
  if (!isDev || !show) {
    return null;
  }

  return (
    <div className="absolute top-1 right-1 z-50 pointer-events-none">
      <div className="bg-background/90 backdrop-blur-sm border rounded-md p-2 text-[10px] font-mono space-y-1 shadow-sm">
        {/* Mode indicator */}
        <div className="flex items-center gap-1">
          <Activity className="h-3 w-3 text-muted-foreground" />
          <span className="text-muted-foreground">Mode:</span>
          <Badge 
            variant={mode === "live" ? "default" : "secondary"} 
            className="text-[9px] h-4 px-1"
          >
            {mode}
          </Badge>
        </div>

        {/* Deal type */}
        {dealType && (
          <div className="flex items-center gap-1">
            <Layers className="h-3 w-3 text-muted-foreground" />
            <span className="text-muted-foreground">Type:</span>
            <span className="text-foreground">{dealType}</span>
          </div>
        )}

        {/* Draft hash */}
        {draftHash && (
          <div className="flex items-center gap-1">
            <Hash className="h-3 w-3 text-muted-foreground" />
            <span className="text-muted-foreground">Hash:</span>
            <span className="text-foreground">{draftHash}</span>
          </div>
        )}

        {/* Render count */}
        {renderCount !== undefined && (
          <div className="flex items-center gap-1">
            <Zap className="h-3 w-3 text-muted-foreground" />
            <span className="text-muted-foreground">Renders:</span>
            <span className="text-foreground">{renderCount}</span>
          </div>
        )}

        {/* Render time */}
        {renderTimeMs !== undefined && (
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3 text-muted-foreground" />
            <span className="text-muted-foreground">Time:</span>
            <span className="text-foreground">{renderTimeMs.toFixed(1)}ms</span>
          </div>
        )}

        {/* Last action */}
        {lastAction && (
          <div className="flex items-start gap-1 max-w-[150px]">
            <span className="text-muted-foreground shrink-0">Last:</span>
            <span className="text-foreground truncate" title={lastAction}>
              {lastAction.length > 30 ? lastAction.slice(0, 30) + "…" : lastAction}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default PreviewDiagnosticsOverlay;
